import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Import all child components
import ProspectsHeader from './ProspectsHeader';
import ProspectsTable from './ProspectsTable';
import Pagination from './Pagination';

// Import your other components
import HeaderMainPage from '../../header/HeaderMainPage';

/* =======================
   MOCK HELPERS (fallback)
   ======================= */
const MOCK_TOTAL_PROSPECTS = 137; // tweak to change how many pages exist when using mocks

const MOCK_INDUSTRIES = [
  "SaaS","Fintech","Healthcare","E-commerce","Edtech",
  "Manufacturing","Energy","Retail","Logistics","Cybersecurity"
];
const MOCK_TITLES = [
  "Head of Growth","VP Sales","Director of Marketing",
  "Sales Manager","Chief Revenue Officer","Founder",
  "Product Marketing Manager","Business Development Lead"
];
const FIRST_NAMES = [
  "Aarav","Isha","Kabir","Maya","Vihaan","Anaya","Ethan","Olivia",
  "Liam","Sophia","Noah","Ava","Arjun","Sara","Dev","Kiara"
];
const LAST_NAMES = [
  "Sharma","Patel","Iyer","Kapoor","Gupta","Mehta","Verma","Rao",
  "Singh","Nair","Das","Khan","Bose","Malhotra","Banerjee","Menon"
];

// deterministic pseudo-random so each page looks stable
function prng(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function pick(arr, seedBump) {
  const idx = Math.floor(prng(seedBump) * arr.length);
  return arr[idx];
}
function makeCompanyName(seed) {
  const nouns = ["Analytics","Systems","Dynamics","Labs","Solutions","Cloud","Networks","Tech","Digital","Works"];
  const prefixes = ["Robo","Hyper","Blue","Neo","Quantum","Nova","Prime","Vertex","Peak","Core"];
  return `${pick(prefixes, seed + 0.13)} ${pick(nouns, seed + 0.77)}`;
}
function generateMockProspects({ page, pageSize }) {
  const start = (page - 1) * pageSize;
  const end = Math.min(start + pageSize, MOCK_TOTAL_PROSPECTS);
  const items = [];
  for (let i = start; i < end; i++) {
    const seed = i + 1;
    const repFirst = pick(FIRST_NAMES, seed + 0.21);
    const repLast  = pick(LAST_NAMES,  seed + 0.42);
    const repName  = `${repFirst} ${repLast}`;
    const company  = makeCompanyName(seed + 0.9);
    const industry = pick(MOCK_INDUSTRIES, seed + 0.33);
    const title    = pick(MOCK_TITLES,    seed + 0.55);

    const emailStatuses = [0, 1, 2]; // 0=unknown, 1=confirmed, 2=bounced
    const emailStatus   = emailStatuses[Math.floor(prng(seed + 0.66) * emailStatuses.length)];
    const stages = ["Not Started","Queued","In Progress","Paused","Completed"];
    const outreach = pick(stages, seed + 0.77);
    const linkedin = pick(stages, seed + 0.88);

    const avatar = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(repName)}&radius=50`;

    items.push({
      company_id: `mock-${seed}`,
      company_name: company,
      industry,
      rep_name: repName,
      rep_title: title,
      avatar_img: avatar,
      email_confirmation_status_id: emailStatus,
      outreach_progress: outreach,
      linkedin_outreach_progress: linkedin,
      isMockData: true,
    });
  }
  return items;
}

const ProspectsPage = () => {
  const navigate = useNavigate();

  // --- Core Data State ---
  const [prospects, setProspects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [numProspecttoView, setNumProspecttoView] = useState(20);

  // --- UI & Interaction State ---
  const [selectedRows, setSelectedRows] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [autopilotStatus, setAutoPilotStatus] = useState(false);

  const Campaign_Name = localStorage.getItem("campaign_name") || "Your Campaign";

  // --- API INTEGRATION & DATA FETCHING (with mock fallback) ---
  useEffect(() => {
    const fetchProspects = async () => {
      setIsLoading(true);
      setSelectAll(false);
      setSelectedRows({});

      const token = localStorage.getItem('access_token');
      const roboman_api = process.env.REACT_APP_ROBOMAN_API;
      const campaignId = localStorage.getItem("campaign_id") || 'b2faf376-d6a4-4489-bdea-13b6ca28a39d';
      const apiUrl = `${roboman_api}/campaign/${campaignId}/fetch_prospects/${currentPage}/${numProspecttoView}`;
      console.log("Fetching Prospects from:", apiUrl);

      const finalizeWithMocks = (reason = "No API data") => {
        console.warn(`[Mock Data] Using mocks: ${reason}`);
        const mocks = generateMockProspects({
          page: Number(currentPage) || 1,
          pageSize: Number(numProspecttoView) || 10,
        });
        setProspects(mocks);
        setTotalPages(Math.max(1, Math.ceil(MOCK_TOTAL_PROSPECTS / (Number(numProspecttoView) || 10))));
      };

      try {
        // If no token, keep the page visible with mock data instead of redirecting.
        if (!token) {
          finalizeWithMocks("Missing token");
          return;
        }

        const response = await fetch(apiUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          finalizeWithMocks(`API error ${response.status} ${response.statusText}`);
          return;
        }

        const data = await response.json();
        console.log("Fetched Prospects Data:", data);

        // Flexible API shapes
        const rawProspects =
          data?.prospects_data ||
          data?.prospects ||
          (Array.isArray(data) ? data : []);

        if (!rawProspects || rawProspects.length === 0) {
          finalizeWithMocks("API returned 0 rows");
          return;
        }

        const mappedData = rawProspects.map((p, idx) => {
          const repName = p.representative_name || p.rep_name || "Unknown Rep";
          const avatar  = p.avatar_img || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(repName)}&radius=50`;
          return {
            company_id: p.id ?? p.company_id ?? `api-${currentPage}-${idx}`,
            company_name: p.company ?? p.company_name ?? "Unnamed Company",
            industry: p.industry ?? "—",
            rep_name: repName,
            rep_title: p.representative_title ?? p.rep_title ?? "—",
            avatar_img: avatar,
            email_confirmation_status_id: p.email_confirmation_status_id ?? 1,
            outreach_progress: p.outreach_progress ?? 'Not Started',
            linkedin_outreach_progress: p.linkedin_outreach_progress ?? 'Not Started',
            isMockData: false,
          };
        });

        setProspects(mappedData);

        // Prefer API totals if present; otherwise keep a sensible pagination with mocks baseline.
        const apiTotal = data?.total_pages || data?.total || data?.count;
        if (typeof apiTotal === "number") {
          const pages = data?.total_pages
            ? data.total_pages
            : Math.max(1, Math.ceil(apiTotal / (Number(numProspecttoView) || 10)));
          setTotalPages(pages);
        } else {
          setTotalPages(Math.max(1, Math.ceil(MOCK_TOTAL_PROSPECTS / (Number(numProspecttoView) || 10))));
        }
      } catch (error) {
        console.error("Failed to fetch prospects:", error);
        finalizeWithMocks(error.message || "Network/Parse error");
        Swal?.fire?.({
          icon: 'warning',
          title: 'Using Mock Data',
          text: 'Backend is unavailable—showing sample data so you can keep working on the design.',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProspects();
    // NOTE: we intentionally do NOT include `navigate` in deps, and we keep the page showing even if token is missing
  }, [currentPage, numProspecttoView]);

  // --- HANDLER FUNCTIONS ---

  const handleBack = () => navigate('/my-campaign');
  const handleRefreshPage = () => setCurrentPage(1);

  // Table Selection
  const handleRowSelect = (id) => {
    const newSelectedRows = { ...selectedRows, [id]: !selectedRows[id] };
    setSelectedRows(newSelectedRows);
    setSelectAll(prospects.length > 0 && prospects.every(p => newSelectedRows[p.company_id]));
  };

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    const newSelectedRows = {};
    if (isChecked) {
      prospects.forEach(p => { newSelectedRows[p.company_id] = true; });
    }
    setSelectedRows(newSelectedRows);
  };

  // Header Actions
  const handleViewSummary = () => Swal.fire('Summary Clicked!', 'Implement handleViewSummary logic.');
  const handleUpdateCampaign = () => navigate('/update-campaign');
  const handleAddMatchingCompany = () => navigate('/addmatching');
  const handleAddContact = () => Swal.fire('Add Contact!', 'Implement logic to open your Add Contact modal.');
  const handlePromptConfig = () => Swal.fire('Prompt Config!', 'Implement logic to open your Prompt Config modal.');
  const handleSetupMeeting = () => Swal.fire('Setup Meeting!', 'Implement logic to open your Setup Meeting modal.');

  // Bulk Actions
  const handleGenerateSelected = () => Swal.fire('Generate for Selected', `IDs: ${Object.keys(selectedRows).join(', ')}`);
  const handleGenerateWithPrompt = () => Swal.fire('Generate with Prompt', 'Open prompt modal then generate.');
  const handleSendEmailSelected = () => Swal.fire('Send Email to Selected', `IDs: ${Object.keys(selectedRows).join(', ')}`);
  const handleSendLinkedinSelected = () => Swal.fire('Send LinkedIn to Selected', `IDs: ${Object.keys(selectedRows).join(', ')}`);
  const handleDeleteSelected = () => Swal.fire({ title: 'Delete Selected?', text: `IDs: ${Object.keys(selectedRows).join(', ')}`, icon: 'warning', showCancelButton: true });

  // Autopilot
  const handleStartAutopilot = () => {
    Swal.fire('Starting Autopilot!', '', 'success');
    setAutoPilotStatus(true);
  };
  const handleStopAutopilot = () => {
    Swal.fire('Stopping Autopilot!', '', 'info');
    setAutoPilotStatus(false);
  };

  // View Count
  const handleSetViewCount = (count) => {
    setNumProspecttoView(count);
    setCurrentPage(1);
  };

  // Row-specific actions
  const handleGenerate = (id) => Swal.fire('Generate Action', `For ID: ${id}`);
  const handleReview = (id) => Swal.fire('Review Action', `For ID: ${id}`);
  const handleSend = (id) => Swal.fire('Send Email Action', `For ID: ${id}`);
  const handleSendLinked = (id) => Swal.fire('Send LinkedIn Action', `For ID: ${id}`);

  return (
    <div className="bg-slate-50 min-h-screen">
      <HeaderMainPage />
      <main className="w-full max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <ProspectsHeader
          campaignName={Campaign_Name}
          autopilotStatus={autopilotStatus}
          onBack={handleBack}
          onRefresh={handleRefreshPage}
          onViewSummary={handleViewSummary}
          onMailboxClick={() => navigate("/unibox")}
          onUpdateCampaign={handleUpdateCampaign}
          onAddMatchingCompany={handleAddMatchingCompany}
          onAddContact={handleAddContact}
          onPromptConfig={handlePromptConfig}
          onSetupMeeting={handleSetupMeeting}
          onGenerateSelected={handleGenerateSelected}
          onGenerateWithPrompt={handleGenerateWithPrompt}
          onSendEmailSelected={handleSendEmailSelected}
          onSendLinkedinSelected={handleSendLinkedinSelected}
          onDeleteSelected={handleDeleteSelected}
          onStartAutopilot={handleStartAutopilot}
          onStopAutopilot={handleStopAutopilot}
          onSetViewCount={handleSetViewCount}
        />
        <div className="mt-6 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <ProspectsTable
            isLoading={isLoading}
            prospects={prospects}
            selectedRows={selectedRows}
            selectAll={selectAll}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAll}
            onGenerate={handleGenerate}
            onReview={handleReview}
            onSend={handleSend}
            onSendLinked={handleSendLinked}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onNextPage={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            onPrevPage={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            onPageChange={(num) => setCurrentPage(num)}
          />
        </div>
      </main>
    </div>
  );
};

export default ProspectsPage;
